/** Helpers compartilhados do formulário de lead (Pipeline + Leads). */

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';

export const LEAD_TYPE_OPTIONS = [
  { value: 'help', label: 'Ajuda' },
  { value: 'broker', label: 'Falar com corretor' },
  { value: 'sell', label: 'Vender imóvel' },
  { value: 'buy', label: 'Comprar imóvel' },
  { value: 'rent', label: 'Alugar imóvel' },
  { value: 'interest', label: 'Interesse em imóvel' },
  { value: 'avaliacao', label: 'Avaliação' },
  { value: 'outros', label: 'Outros' },
  { value: 'support', label: 'Suporte técnico' }
];

export const LEAD_HEAT_OPTIONS = [
  { value: '', label: 'Automático' },
  { value: 'quente', label: 'Quente' },
  { value: 'morno', label: 'Morno' },
  { value: 'frio', label: 'Frio' }
];

export const LEAD_SOURCE_DISPLAY = {
  chat: 'Chat',
  manual: 'Manual',
  pipeline: 'Pipeline',
  landing: 'Landing page',
  property: 'Página do imóvel',
  contact: 'Formulário de contato',
  contact_form: 'Formulário de contato'
};

export const MAX_NEGOTIATION_VALUE_DIGITS = 11;

export function getSourceDisplay(source) {
  return LEAD_SOURCE_DISPLAY[source] || source || 'Não informado';
}

export function formatPhoneInput(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function normalizePropertyCodes(propertyCode) {
  if (!propertyCode) return [];
  if (Array.isArray(propertyCode)) {
    return propertyCode
      .map((item) => {
        if (typeof item === 'string') return { code: item };
        if (item && typeof item === 'object') return item;
        return null;
      })
      .filter(Boolean);
  }
  if (typeof propertyCode === 'string') {
    return propertyCode
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)
      .map((code) => ({ code }));
  }
  return [];
}

export function isPropertySelected(code, propertyCodes) {
  return normalizePropertyCodes(propertyCodes).some((p) => p.code === code);
}

export function buildPropertyDataFromSearch(property) {
  return {
    id: property.id,
    code: property.code,
    title: property.title,
    city: property.city,
    neighborhood: property.neighborhood,
    street: property.street,
    number: property.number,
    coverImage:
      property.coverImage ||
      (Array.isArray(property.images) && property.images.length > 0 ? property.images[0] : '') ||
      '',
    purpose: property.purpose || 'sale',
    price: property.price ?? property.preco ?? property.valor ?? property.value ?? null,
    rent: property.rent ?? null,
    preco: property.preco ?? property.price ?? null,
    valor: property.valor ?? null,
    value: property.value ?? null
  };
}

/** Opções searchable (CustomDropdown) a partir da lista de imóveis */
export function buildLeadPropertyOptions(properties = []) {
  return properties
    .filter((p) => p?.code)
    .map((p) => {
      const propertyData = buildPropertyDataFromSearch(p);
      const location = [p.city, p.neighborhood].filter(Boolean).join(' · ') || p.address || null;
      return {
        value: propertyData.code,
        label: propertyData.code,
        subtitle: [propertyData.title, location].filter(Boolean).join(' · ') || undefined,
        searchText: [propertyData.code, propertyData.title, p.city, p.neighborhood, p.street, p.address]
          .filter(Boolean)
          .join(' '),
        propertyData
      };
    });
}

/** Opções searchable (CustomDropdown) a partir da lista de corretores */
export function buildLeadBrokerOptions(brokers = []) {
  return brokers.map((b) => {
    const label = b.name || b.displayName || 'Sem nome';
    const email = b.email && b.email !== label ? b.email : null;
    const phone = b.phone || b.whatsapp || null;
    return {
      value: label,
      label,
      subtitle: [email, phone].filter(Boolean).join(' · ') || undefined,
      searchText: [label, b.email, phone].filter(Boolean).join(' ')
    };
  });
}

function propertyNeedsDisplayEnrichment(item) {
  if (!item || !item.code) return false;
  const hasImage = Boolean(item.coverImage);
  const hasTitle = Boolean(item.title);
  const hasAddress = Boolean(item.city || item.neighborhood || item.street);
  return !hasImage || !hasTitle || !hasAddress;
}

/**
 * Completa imóveis vinculados incompletos (só código / sem foto) buscando no Firestore.
 */
export async function enrichPropertyCodesForDisplay(propertyCodes, firestoreDb) {
  const codes = normalizePropertyCodes(propertyCodes);
  if (!codes.length || !firestoreDb) return codes;

  const enriched = await Promise.all(
    codes.map(async (item) => {
      if (!propertyNeedsDisplayEnrichment(item)) return item;

      try {
        if (item.id) {
          const snap = await getDoc(doc(firestoreDb, 'properties', item.id));
          if (snap.exists()) {
            const data = snap.data();
            return {
              ...item,
              ...buildPropertyDataFromSearch({ id: snap.id, ...data }),
              price: item.price ?? item.preco ?? null,
              rent: item.rent ?? null,
              value: item.value ?? null,
              purpose: item.purpose || data.purpose || 'sale'
            };
          }
        }

        const code = String(item.code || '').trim();
        if (!code) return item;

        let snap = await getDocs(
          query(collection(firestoreDb, 'properties'), where('code', '==', code))
        );
        if (snap.empty && code !== code.toUpperCase()) {
          snap = await getDocs(
            query(collection(firestoreDb, 'properties'), where('code', '==', code.toUpperCase()))
          );
        }
        if (snap.empty) return item;

        const d = snap.docs[0];
        const data = d.data();
        return {
          ...item,
          ...buildPropertyDataFromSearch({ id: d.id, ...data }),
          price: item.price ?? item.preco ?? null,
          rent: item.rent ?? null,
          value: item.value ?? null,
          purpose: item.purpose || data.purpose || 'sale'
        };
      } catch (err) {
        console.error('Erro ao enriquecer imóvel vinculado:', err);
        return item;
      }
    })
  );

  return enriched;
}

export function formatCurrency(value, options = {}) {
  if (value === null || value === undefined || value === '') return '';
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
  return options.monthly ? `${formatted}/mês` : formatted;
}

export function isFirstPropertyRent(propertyCode) {
  const first = normalizePropertyCodes(propertyCode)[0];
  return (first?.purpose || '').toLowerCase() === 'rent';
}

export function parseCurrencyToNumber(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;

  const s = String(raw)
    .trim()
    .replace(/\/\s*m[eê]s/gi, '')
    .replace(/[R$\s]/gi, '')
    .replace(/[^0-9,.-]/g, '');

  if (!s) return null;

  if (s.includes(',')) {
    const normalized = s.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : null;
  }

  const n = parseFloat(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

export function getFirstPropertyPriceFromPropertyCode(propertyCode) {
  const codes = normalizePropertyCodes(propertyCode);
  const first = codes[0];
  if (!first) return null;
  const purpose = first.purpose || 'sale';
  const rawCandidates =
    purpose === 'rent'
      ? [
          first.rent,
          first.value,
          first.valor,
          first.preco,
          first.price,
          first.precoVenda,
          first.precoCompra,
          first.negotiationValue
        ]
      : [
          first.value,
          first.valor,
          first.preco,
          first.price,
          first.precoVenda,
          first.precoCompra,
          first.rent,
          first.negotiationValue
        ];
  for (const cand of rawCandidates) {
    const n = parseCurrencyToNumber(cand);
    if (n !== null) return n;
  }
  if (typeof first.title === 'string') {
    const n = parseCurrencyToNumber(first.title);
    if (n !== null) return n;
  }
  return null;
}

export function parseNegotiationValueForSave(raw) {
  if (raw === null || raw === undefined || String(raw).trim() === '') return null;
  const s = String(raw).trim();
  const n = parseCurrencyToNumber(s);
  if (n !== null) return n;
  const n2 = parseFloat(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n2) ? n2 : null;
}

export function formatNegotiationValueInput(raw, propertyCode) {
  let digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length > MAX_NEGOTIATION_VALUE_DIGITS) {
    digits = digits.slice(0, MAX_NEGOTIATION_VALUE_DIGITS);
  }
  return formatCurrency(parseInt(digits, 10) / 100, {
    monthly: isFirstPropertyRent(propertyCode)
  });
}

export function formatValueForForm(raw, propertyCode) {
  const monthly = isFirstPropertyRent(propertyCode);
  if (raw !== null && raw !== undefined && raw !== '') {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return formatCurrency(raw, { monthly });
    }
    const parsed = parseCurrencyToNumber(String(raw));
    if (parsed !== null) return formatCurrency(parsed, { monthly });
    return String(raw);
  }
  const fromProperty = getFirstPropertyPriceFromPropertyCode(propertyCode);
  if (fromProperty !== null) return formatCurrency(fromProperty, { monthly });
  return '';
}

function getCardReferenceDate(card) {
  const updatedDate = card?.updatedAt?.toDate
    ? card.updatedAt.toDate()
    : card?.updatedAt
      ? new Date(card.updatedAt)
      : null;
  const createdDate = card?.createdAt?.toDate
    ? card.createdAt.toDate()
    : card?.createdAt
      ? new Date(card.createdAt)
      : null;
  return updatedDate || createdDate || null;
}

export function getLeadHeatLevel(card) {
  const manualHeat = (card?.leadHeatOverride || '').toLowerCase().trim();
  if (manualHeat === 'quente' || manualHeat === 'morno' || manualHeat === 'frio') {
    return manualHeat;
  }

  const refDate = getCardReferenceDate(card);
  if (!refDate || Number.isNaN(refDate.getTime())) return 'frio';

  const days = Math.floor((Date.now() - refDate.getTime()) / 86400000);
  if (days <= 2) return 'quente';
  if (days <= 7) return 'morno';
  return 'frio';
}

export function getEffectiveLeadHeatForForm(card, formOverride = '') {
  const manual = String(formOverride ?? '').toLowerCase().trim();
  if (manual === 'quente' || manual === 'morno' || manual === 'frio') {
    return manual;
  }
  if (!card) return '';
  return getLeadHeatLevel(card);
}

/** Metadados visuais da temperatura do lead (lista / badges). */
export function getLeadHeatMeta(card) {
  const level = getLeadHeatLevel(card);
  if (level === 'quente') {
    return {
      level: 'quente',
      label: 'Quente',
      color: '#ef4444',
      className:
        'bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800'
    };
  }
  if (level === 'morno') {
    return {
      level: 'morno',
      label: 'Morno',
      color: '#f59e0b',
      className:
        'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800'
    };
  }
  return {
    level: 'frio',
    label: 'Frio',
    color: '#3b82f6',
    className:
      'bg-sky-100 text-sky-800 border border-sky-200 dark:bg-sky-900/40 dark:text-sky-300 dark:border-sky-800'
  };
}

export function formatCreatedAtDisplay(createdAt) {
  if (!createdAt) return '-';
  const date = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function isNegotiationValueEmpty(value) {
  return !String(value ?? '').trim();
}
